<template>
  <Sidebar collapsible="icon">
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            as-child
            size="lg"
            tooltip="IDP"
          >
            <router-link to="/idp">
              <span
                class="bg-agent flex size-8 shrink-0 items-center justify-center rounded-lg text-white"
              >
                <Layers class="size-4" />
              </span>
              <span class="flex flex-col leading-tight">
                <span class="text-sm font-semibold">IDP</span>
                <span class="text-xs text-muted-foreground">HighLevel</span>
              </span>
            </router-link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupLabel>Platform</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                as-child
                :is-active="route.path === '/idp'"
                tooltip="Home"
              >
                <router-link to="/idp"><Home /><span>Home</span></router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem v-if="catalog">
              <SidebarMenuButton
                as-child
                :is-active="route.path === '/idp/services' || route.path === '/idp/team'"
                :tooltip="catalog.name"
              >
                <router-link :to="catalog.path"
                  ><Server /><span>{{ catalog.name }}</span></router-link
                >
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                as-child
                :is-active="route.path === '/idp/workspace' || route.path === '/idp/subteam'"
                :tooltip="workspaceHint"
                class="h-auto"
              >
                <router-link :to="workspaceTo">
                  <Folder />
                  <span class="flex min-w-0 flex-col leading-tight">
                    <span class="truncate">Workspace</span>
                    <span class="truncate text-xs text-muted-foreground">{{ workspaceHint }}</span>
                  </span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup v-if="onTeam && teamNav">
        <SidebarGroupLabel class="truncate">{{ teamNav.name }}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                as-child
                :is-active="route.query.view !== 'overview'"
                tooltip="Actionable"
              >
                <router-link :to="teamLink('insights')"><Lightbulb /><span>Actionable</span></router-link>
              </SidebarMenuButton>
              <SidebarMenuBadge v-if="teamNav.insights !== null">{{ teamNav.insights }}</SidebarMenuBadge>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton
                as-child
                :is-active="route.query.view === 'overview'"
                tooltip="Overview"
              >
                <router-link :to="teamLink('overview')"
                  ><LayoutDashboard /><span>Overview</span></router-link
                >
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup v-if="onSubTeam && teamNav?.subTeam">
        <SidebarGroupLabel class="truncate">{{ teamNav.name }} · {{ teamNav.subTeam.name }}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem
              v-for="v in SUB_TEAM_VIEWS"
              :key="v.view"
            >
              <SidebarMenuButton
                as-child
                :is-active="subTeamView === v.view"
                :tooltip="v.label"
              >
                <router-link :to="subTeamLink(v.view)"
                  ><component :is="v.icon" /><span>{{ v.label }}</span></router-link
                >
              </SidebarMenuButton>
              <SidebarMenuBadge v-if="v.view === 'insights' && teamNav.insights !== null">{{
                teamNav.insights
              }}</SidebarMenuBadge>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup v-if="onWorkspace && workloadNav">
        <SidebarGroupLabel class="truncate font-mono">{{ workloadNav.service }}</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem
              v-for="t in WORKSPACE_TABS"
              :key="t.tab"
            >
              <SidebarMenuButton
                as-child
                :is-active="workspaceTab === t.tab"
                :tooltip="t.label"
              >
                <router-link :to="workspaceLink(t.tab)"
                  ><component :is="t.icon" /><span>{{ t.label }}</span></router-link
                >
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupLabel>Explore</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem
              v-for="tool in explore"
              :key="tool.path"
            >
              <SidebarMenuButton
                as-child
                :is-active="route.path === tool.path"
                :tooltip="tool.name"
              >
                <router-link :to="tool.path">
                  <component :is="ICONS[tool.path] ?? tool.icon" /><span>{{ tool.name }}</span>
                </router-link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>

    <SidebarFooter>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            as-child
            tooltip="Developer Tools"
          >
            <router-link to="/"><ChevronLeft /><span>Developer Tools</span></router-link>
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem v-if="user">
          <SidebarMenuButton
            size="lg"
            :tooltip="user.email"
            class="cursor-default hover:bg-transparent active:bg-transparent"
          >
            <Avatar class="size-8 rounded-lg">
              <AvatarFallback class="rounded-lg bg-primary/10 text-xs font-semibold text-primary">{{
                initials
              }}</AvatarFallback>
            </Avatar>
            <span class="flex min-w-0 flex-col leading-tight">
              <span class="truncate text-sm font-medium">{{ user.name }}</span>
              <span class="truncate text-xs text-muted-foreground">{{ user.email }}</span>
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip="Logout"
            class="text-muted-foreground hover:text-critical"
            @click="auth.logout()"
          >
            <LogOut /><span>Logout</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>

    <SidebarRail />
  </Sidebar>
</template>

<script setup lang="ts">
import { computed, watch, type Component } from 'vue'
import { useRoute } from 'vue-router'
import {
  Boxes,
  Database,
  FileCode,
  Link,
  Wallet,
  Gauge,
  Hammer,
  Layers2,
  ChevronLeft,
  Folder,
  Home,
  LayoutDashboard,
  Layers,
  Lightbulb,
  LogOut,
  Package,
  Server,
  ShieldAlert,
  Waypoints
} from 'lucide-vue-next'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail
} from '@/components/ui/sidebar'
import auth from '../../services/auth/auth'
import { isAdmin, isCostExplorerEditor } from '../../utils/adminUtils'
import { visibleIdpSection } from '../../config/navigation'
import { initialsOf } from '../../pages/idp/components/teamRow'
import { teamNav, workloadNav } from '../../services/idp/idpPageNav'
import {
  idpWorkspaceHint,
  idpWorkspaceTarget,
  rememberIdpScope,
  useIdpLastScope
} from '../../services/idp/idpLastScope'

const ICONS: Record<string, Component> = {
  '/idp/services': Server,
  '/idp/graph': Waypoints,
  '/idp/packages': Package
}

const route = useRoute()
const user = computed(() => auth.getUser())
const initials = computed(() => (user.value?.name ? initialsOf(user.value.name) : ''))

const tools = computed(
  () =>
    visibleIdpSection({
      isAdmin: isAdmin(user.value),
      canEditCostExplorer: isCostExplorerEditor(user.value)
    })?.tools ?? []
)
const catalog = computed(() => tools.value.find((t) => t.path === '/idp/services'))
const explore = computed(() => tools.value.filter((t) => t.path !== '/idp/services'))

const onTeam = computed(() => route.path === '/idp/team')

const WORKSPACE_TABS = [
  { tab: 'overview', label: 'Overview', icon: Gauge },
  { tab: 'build', label: 'Build', icon: Hammer },
  { tab: 'apidocs', label: 'API contracts', icon: FileCode },
  { tab: 'tiering', label: 'API tiering', icon: Layers2 },
  { tab: 'dependencies', label: 'Dependencies', icon: Waypoints }
] as const
const onWorkspace = computed(() => route.path === '/idp/workspace')
const workspaceTab = computed(() => {
  const t = typeof route.query.tab === 'string' ? route.query.tab : ''
  return WORKSPACE_TABS.some((x) => x.tab === t) ? t : 'overview'
})
const workspaceLink = (tab: string) => ({
  path: '/idp/workspace',
  query: { ...workloadNav.value?.query, tab }
})
// Scope carries across, like every other IDP link.
const teamLink = (view: 'insights' | 'overview') => ({
  path: '/idp/team',
  query: {
    ...(typeof route.query.env === 'string' ? { env: route.query.env } : {}),
    ...(typeof route.query.cluster === 'string' ? { cluster: route.query.cluster } : {}),
    team: teamNav.value?.key,
    ...(view === 'overview' ? { view } : {})
  }
})

const SUB_TEAM_VIEWS = [
  { view: 'insights', label: 'Actionable', icon: Lightbulb },
  { view: 'workloads', label: 'Workloads', icon: Boxes },
  { view: 'resources', label: 'Resources', icon: Database },
  { view: 'links', label: 'Links', icon: Link },
  { view: 'cost', label: 'Cost', icon: Wallet },
  { view: 'violations', label: 'IDOR Violations', icon: ShieldAlert }
] as const
const onSubTeam = computed(() => route.path === '/idp/subteam')
const subTeamView = computed(() => {
  const v = typeof route.query.view === 'string' ? route.query.view : ''
  return SUB_TEAM_VIEWS.some((x) => x.view === v) ? v : 'insights'
})
const subTeamLink = (view: string) => ({
  path: '/idp/subteam',
  query: {
    ...(typeof route.query.env === 'string' ? { env: route.query.env } : {}),
    ...(typeof route.query.cluster === 'string' ? { cluster: route.query.cluster } : {}),
    team: teamNav.value?.key,
    subTeam: teamNav.value?.subTeam?.key,
    ...(view === 'insights' ? {} : { view })
  }
})

const lastScope = useIdpLastScope()
const workspaceTo = computed(() => idpWorkspaceTarget(lastScope.value))
const workspaceHint = computed(() => idpWorkspaceHint(lastScope.value))

watch(
  () => [
    route.path,
    route.query.team,
    route.query.subTeam,
    route.query.service,
    route.query.env,
    route.query.cluster
  ],
  () => rememberIdpScope(route.query),
  { immediate: true }
)
</script>
